import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button, Textarea, Alert,
} from '@parsina/framework';

import { CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';

// «پایان ویزیت» — one step, several warnings, and no gate.
//
// core.md §6 rule 3: «Closing needs nothing.» The doctor app's single-step
// closure is kept deliberately, and the dentist app's consent gate — which used
// to leave visits half-closed — is deliberately NOT. Everything this dialog says
// is a warning: an empty note, no diagnoses, a review sheet left open. A student
// who wants to close an empty visit is allowed to; they are simply told what
// they are closing.
//
// ── The order of operations is the whole point ───────────────────────────
//
//   flush pending autosave  →  endEncounter  →  navigate
//
// and not one of those steps may be reordered. The note is debounced by 800 ms,
// so «پایان ویزیت» pressed mid-sentence would otherwise end a visit whose last
// paragraph is still in a timer — and once the visit is closed the store refuses
// the write (`ENCOUNTER_CLOSED`), so the paragraph would be unsaveable, not just
// unsaved. If the flush fails the dialog STAYS OPEN showing the save error: the
// student's words are still on screen behind it, and closing over them would
// discard the one thing that could not be replaced.

export default function CloseVisitDialog({
  open, onClose, warnings = [], defaultSummary = '', onConfirm,
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = React.useState(defaultSummary);
  const [busy, setBusy] = React.useState(false);
  const [saveError, setSaveError] = React.useState(null);

  React.useEffect(() => {
    if (open) { setSummary(defaultSummary); setSaveError(null); }
  }, [open, defaultSummary]);

  if (!open) return null;

  const confirm = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      const result = await onConfirm(summary.trim());
      // `onConfirm` answers `{ ok: false, error }` when the FLUSH failed. The
      // dialog stays where it is; nothing was closed.
      if (result && result.ok === false) setSaveError(result.error || new Error('save failed'));
    } catch (err) {
      setSaveError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent size="md" data-testid="close-visit-dialog">
        <DialogHeader>
          <DialogTitle>{t('visit.close.title')}</DialogTitle>
        </DialogHeader>

        {!!warnings.length && (
          <Alert variant="warning" title={t('visit.close.warningsTitle')} data-testid="close-warnings">
            <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.9 }}>
              {warnings.map((w) => <li key={w}>{t(`visit.close.warning.${w}`)}</li>)}
            </ul>
            <p style={{ margin: '6px 0 0', fontSize: 11.5, color: CT.mute }}>
              {t('visit.close.warningsNote')}
            </p>
          </Alert>
        )}

        <Textarea
          label={t('visit.close.summary')}
          helperText={t('visit.close.summaryHint')}
          rows={4}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          style={{ marginTop: 12 }}
          data-testid="close-summary"
        />

        {saveError && (
          <Alert variant="error" title={t('visit.close.saveFailed')} data-testid="close-save-error">
            {saveError.message}
          </Alert>
        )}

        <DialogFooter>
          <Button onClick={confirm} disabled={busy} data-testid="close-confirm">
            {busy ? t('visit.close.ending') : t('visit.end')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t('common.actions.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
